export interface QuarterChange {
  quarter: string;
  version: string;
  before_version?: string;
  plans: {
    added: Array<{ 
      id: string; 
      name: string; 
      charges?: Array<{ id: string; name: string; details: string }>;
      discounts?: Array<{ id: string; name: string; details: string }>;
    }>;
    removed: Array<{ 
      id: string; 
      name: string; 
      charges?: Array<{ id: string; name: string; details: string }>;
      discounts?: Array<{ id: string; name: string; details: string }>;
    }>;
  };
  charges: {
    added: Array<{ id: string; name: string; planName: string }>;
    removed: Array<{ id: string; name: string; planName: string }>;
  };
  discounts: {
    added: Array<{ id: string; name: string; planName: string }>;
    removed: Array<{ id: string; name: string; planName: string }>;
  };
  consolidated: {
    added: Array<{ id: string; description: string; type: 'plan' | 'charge' | 'discount' }>;
    removed: Array<{ id: string; description: string; type: 'plan' | 'charge' | 'discount' }>;
  };
}

interface PageData {
  quarters?: Array<{ quarter: string; version: string; ids?: string[] }>;
  plans?: Array<{
    id: string;
    plan_name: string;
    display_name?: string;
    charges?: Array<{
      id: string;
      price: string;
      type: string;
    }>;
    discounts?: Array<{
      id: string;
      amount: string;
      type: string;
      amount_type?: string;
      duration?: string;
      description?: string;
    }>;
  }>;
}

/**
 * Detect changes between consecutive quarters for a page
 * @param pageData - The page data containing quarters and plans
 * @returns Array of quarter changes sorted by version (latest first)
 */
export function detectQuarterChanges(pageData: PageData): QuarterChange[] {
  if (!pageData?.quarters || !pageData?.plans) {
    return [];
  }

  // Sort quarters by version (latest first)
  const sortedQuarters = [...pageData.quarters].sort((a, b) => b.version.localeCompare(a.version));
  
  const changes: QuarterChange[] = [];

  // Compare each quarter with the previous one
  for (let i = 0; i < sortedQuarters.length - 1; i++) {
    const currentQuarter = sortedQuarters[i];
    const previousQuarter = sortedQuarters[i + 1];

    const currentIds = new Set(currentQuarter.ids || []);
    const previousIds = new Set(previousQuarter.ids || []);

    // Find added and removed IDs
    const idsAdded = [...currentIds].filter(id => !previousIds.has(id));
    const idsRemoved = [...previousIds].filter(id => !currentIds.has(id));

    if (idsAdded.length > 0 || idsRemoved.length > 0) {
      const change: QuarterChange = {
        quarter: currentQuarter.quarter,
        version: currentQuarter.version,
        before_version: previousQuarter.version,
        plans: { added: [], removed: [] },
        charges: { added: [], removed: [] },
        discounts: { added: [], removed: [] },
        consolidated: { added: [], removed: [] }
      };

      // Group changes by plan
      const planChanges = new Map<string, {
        plan: any;
        charges: { added: any[]; removed: any[] };
        discounts: { added: any[]; removed: any[] };
      }>();

      // Categorize the changes
      [...idsAdded, ...idsRemoved].forEach(id => {
        const isAdded = idsAdded.includes(id);
        const item = findItemById(id, pageData.plans);
        
        if (item) {
          const changeArray = isAdded ? 'added' : 'removed';
          
          if (item.type === 'plan') {
            change.plans[changeArray].push({
              id: item.id,
              name: item.plan_name || item.display_name || 'Unnamed Plan'
            });
          } else if (item.type === 'charge') {
            // Check if this charge's plan is also being added/removed
            const planItem = findItemById(item.planId, pageData.plans);
            const isPlanAlsoChanging = planItem && (idsAdded.includes(item.planId) || idsRemoved.includes(item.planId));
            
            if (isPlanAlsoChanging) {
              // Group with the plan
              if (!planChanges.has(item.planId)) {
                planChanges.set(item.planId, {
                  plan: planItem,
                  charges: { added: [], removed: [] },
                  discounts: { added: [], removed: [] }
                });
              }
              
              planChanges.get(item.planId)!.charges[changeArray].push({
                id: item.id,
                name: formatChargeDetails(item),
                details: item.chargeType || ''
              });
            } else {
              // Show separately
              change.charges[changeArray].push({
                id: item.id,
                name: formatStandaloneChargeName(item),
                planName: item.planName || 'Unknown Plan'
              });
            }
          } else if (item.type === 'discount') {
            // Create descriptive discount name with before/after pricing
            const amount = item.amount ? 
              (item.amount_type === 'percent' ? `${item.amount}%` : `$${item.amount}`) : 'N/A';
            const discountType = item.discountType ? 
              item.discountType.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Discount';
            const duration = item.duration ? ` for ${item.duration}` : '';
            const planName = item.planName || 'Unknown Plan';
            
            // Calculate before/after pricing if charges exist
            let pricingInfo = '';
            if (item.planCharges && item.planCharges.length > 0) {
              // Find the main pricing charge (usually the first one or the one with the highest price)
              const mainCharge = item.planCharges.reduce((max: any, charge: any) => {
                const chargePrice = parseFloat(charge.price || '0');
                const maxPrice = parseFloat(max?.price || '0');
                return chargePrice > maxPrice ? charge : max;
              }, item.planCharges[0]);
              
              if (mainCharge && mainCharge.price) {
                const originalPrice = parseFloat(mainCharge.price);
                let discountedPrice = originalPrice;
                
                if (item.amount_type === 'percent') {
                  const discountPercent = parseFloat(item.amount || '0') / 100;
                  discountedPrice = originalPrice * (1 - discountPercent);
                } else {
                  discountedPrice = originalPrice - parseFloat(item.amount || '0');
                }
                
                // Ensure discounted price doesn't go below 0
                discountedPrice = Math.max(0, discountedPrice);
                
                pricingInfo = ` (${mainCharge.currency || '$'}${originalPrice.toFixed(2)} → ${mainCharge.currency || '$'}${discountedPrice.toFixed(2)})`;
              }
            }
            
            const descriptiveName = `${amount} ${discountType} to ${planName}${duration}${pricingInfo}`;
            
            change.discounts[changeArray].push({
              id: item.id,
              name: descriptiveName,
              planName: item.planName || 'Unknown Plan'
            });
          }
        }
      });

      // Create consolidated descriptions for better UX
      const createConsolidatedDescriptions = (changeType: 'added' | 'removed') => {
        const planChanges = change.plans[changeType];
        const chargeChanges = change.charges[changeType];
        const discountChanges = change.discounts[changeType];

        // Group charges and discounts by plan
        const planMap = new Map<string, { charges: any[], discounts: any[] }>();
        
        // Initialize map with plan changes
        planChanges.forEach(plan => {
          planMap.set(plan.name, { charges: [], discounts: [] });
        });

        // Group charges by plan
        chargeChanges.forEach(charge => {
          if (!planMap.has(charge.planName)) {
            planMap.set(charge.planName, { charges: [], discounts: [] });
          }
          planMap.get(charge.planName)!.charges.push(charge);
        });

        // Group discounts by plan
        discountChanges.forEach(discount => {
          if (!planMap.has(discount.planName)) {
            planMap.set(discount.planName, { charges: [], discounts: [] });
          }
          planMap.get(discount.planName)!.discounts.push(discount);
        });

        // Create consolidated descriptions
        planMap.forEach((data, planName) => {
          let description = '';
          const actionWord = changeType === 'added' ? 'Add' : 'Remove';
          
          // Check if this is a new plan or just charge/discount changes
          const isNewPlan = planChanges.some(p => p.name === planName);
          
          if (isNewPlan) {
            // New plan with details
            description = `${actionWord} new plan: ${planName}`;
            
            if (data.charges.length > 0) {
              const chargeDescriptions = data.charges.map(c => c.name).join(', ');
              description += ` with pricing: ${chargeDescriptions}`;
            }
            
            if (data.discounts.length > 0) {
              const discountDescriptions = data.discounts.map(d => d.name.replace(`to ${planName}`, '')).join(', ');
              description += ` and discounts: ${discountDescriptions}`;
            }
          } else {
            // Just charge or discount changes
            if (data.charges.length > 0 && data.discounts.length === 0) {
              const chargeDescriptions = data.charges.map(c => c.name).join(', ');
              description = `${actionWord} pricing to ${planName}: ${chargeDescriptions}`;
            } else if (data.discounts.length > 0 && data.charges.length === 0) {
              const discountDescriptions = data.discounts.map(d => d.name).join(', ');
              description = `${actionWord} ${discountDescriptions}`;
            } else if (data.charges.length > 0 && data.discounts.length > 0) {
              const chargeDescriptions = data.charges.map(c => c.name).join(', ');
              const discountDescriptions = data.discounts.map(d => d.name.replace(`to ${planName}`, '')).join(', ');
              description = `${actionWord} to ${planName}: pricing ${chargeDescriptions} and discounts ${discountDescriptions}`;
            }
          }

          if (description) {
            change.consolidated[changeType].push({
              id: `consolidated-${planName}-${changeType}`,
              description,
              type: isNewPlan ? 'plan' : (data.charges.length > 0 ? 'charge' : 'discount')
            });
          }
        });
      };

      createConsolidatedDescriptions('added');
      createConsolidatedDescriptions('removed');

      // Only add to changes if there are actual changes
      if (change.plans.added.length > 0 || change.plans.removed.length > 0 ||
          change.charges.added.length > 0 || change.charges.removed.length > 0 ||
          change.discounts.added.length > 0 || change.discounts.removed.length > 0) {
        changes.push(change);
      }
    }
  }

  return changes;
}

/**
 * Format charge details for display
 */
function formatChargeDetails(charge: any): string {
  if (charge.chargeType === 'pricing_not_disclosed') {
    return '"Contact Us" for pricing (i.e. no pricing disclosed)';
  }
  
  if (!charge.price || charge.price === '0') {
    return 'No pricing disclosed';
  }
  
  return `$${charge.price} ${charge.chargeType || ''}`.trim();
}

/**
 * Format charge name for standalone charge changes
 */
function formatStandaloneChargeName(charge: any): string {
  if (charge.chargeType === 'pricing_not_disclosed') {
    return `"Contact Us" charge to ${charge.planName}`;
  }
  
  if (!charge.price || charge.price === '0') {
    return `No pricing charge to ${charge.planName}`;
  }
  
  // Format the charge type for better readability
  let chargeTypeFormatted = '';
  if (charge.chargeType) {
    chargeTypeFormatted = charge.chargeType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l: string) => l.toUpperCase())
      .replace('Monthly Recurring Billed Yearly', 'Monthly (billed yearly)')
      .replace('Yearly Recurring', 'Yearly')
      .replace('Monthly Recurring', 'Monthly')
      .replace('One Time', 'One-time');
  }
  
  return `$${charge.price} ${chargeTypeFormatted} charge to ${charge.planName}`.trim();
}

/**
 * Format discount details for display
 */
function formatDiscountDetails(discount: any): string {
  const amount = discount.amount ? `${discount.amount}%` : 'N/A';
  const type = discount.discountType || 'discount';
  return `${amount} ${type}`.trim();
}

/**
 * Find an item (plan, charge, or discount) by ID
 */
function findItemById(id: string, plans: Array<any>): any {
  // Check plans
  for (const plan of plans) {
    if (plan.id === id) {
      return {
        type: 'plan',
        id: plan.id,
        plan_name: plan.plan_name,
        display_name: plan.display_name
      };
    }

    // Check charges within the plan
    if (plan.charges) {
      for (const charge of plan.charges) {
        if (charge.id === id) {
          return {
            type: 'charge',
            id: charge.id,
            price: charge.price,
            chargeType: charge.type,
            planName: plan.plan_name || plan.display_name || 'Unnamed Plan',
            planId: plan.id
          };
        }
      }
    }

    // Check discounts within the plan
    if (plan.discounts) {
      for (const discount of plan.discounts) {
        if (discount.id === id) {
          return {
            type: 'discount',
            id: discount.id,
            amount: discount.amount,
            amount_type: discount.amount_type,
            duration: discount.duration,
            discountType: discount.type,
            planName: plan.plan_name || plan.display_name || 'Unnamed Plan',
            planCharges: plan.charges || []
          };
        }
      }
    }
  }

  return null;
}

/**
 * Check if there are any changes in the quarters
 */
export function hasQuarterChanges(changes: QuarterChange[]): boolean {
  return changes.length > 0;
}